import { useState, useEffect } from 'react';

interface GoogleMapsApiState {
    isLoaded: boolean;
    isLoading: boolean;
    error: Error | null;
}

// Estado global singleton para a API do Google Maps
let globalApiState: GoogleMapsApiState = {
    isLoaded: false,
    isLoading: false,
    error: null
};

// Promise global para evitar múltiplos carregamentos simultâneos
let loadingPromise: Promise<void> | null = null;

// Lista de callbacks para notificar componentes sobre mudanças de estado
const stateChangeCallbacks = new Set<(state: GoogleMapsApiState) => void>();

/**
 * Carrega a API do Google Maps usando a nova API importLibrary (recomendada)
 * Esta é a implementação mais robusta e atual do Google Maps API
 */
const loadGoogleMapsApi = async (): Promise<void> => {
    // Se já está carregada, retornar imediatamente
    if (globalApiState.isLoaded) {
        return Promise.resolve();
    }

    // Se já está carregando, retornar a Promise existente
    if (loadingPromise) {
        return loadingPromise;
    }

    // Iniciar novo carregamento
    globalApiState = { isLoaded: false, isLoading: true, error: null };
    notifyStateChange();

    loadingPromise = new Promise<void>(async (resolve, reject) => {
        try {
            const apiKey = import.meta.env.VITE_REACT_GOOGLE_MAPS_API_KEY;

            if (!apiKey) {
                const keyError = new Error('VITE_REACT_GOOGLE_MAPS_API_KEY não encontrada nas variáveis de ambiente');
                globalApiState = { isLoaded: false, isLoading: false, error: keyError };
                notifyStateChange();
                reject(keyError);
                return;
            }

            // Verificar se já existe google.maps disponível
            if (window.google?.maps?.importLibrary) {
                try {
                    // Usar a nova API importLibrary para carregar a biblioteca Places
                    await window.google.maps.importLibrary("places");
                    globalApiState = { isLoaded: true, isLoading: false, error: null };
                    notifyStateChange();
                    resolve();
                    return;
                } catch (error) {
                    console.error('Erro ao importar biblioteca Places:', error);
                    // Continuar com o carregamento tradicional se importLibrary falhar
                }
            }

            // Verificar se já existe um script carregando
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                // Aguardar o script existente carregar
                existingScript.addEventListener('load', async () => {
                    try {
                        // Usar importLibrary após o script carregar
                        if (window.google?.maps?.importLibrary) {
                            await window.google.maps.importLibrary("places");
                        }
                        globalApiState = { isLoaded: true, isLoading: false, error: null };
                        notifyStateChange();
                        resolve();
                    } catch (error) {
                        const apiError = new Error('Falha ao importar biblioteca Places após carregamento');
                        globalApiState = { isLoaded: false, isLoading: false, error: apiError };
                        notifyStateChange();
                        reject(apiError);
                    }
                });
                existingScript.addEventListener('error', (error) => {
                    const apiError = new Error('Falha ao carregar Google Maps API');
                    globalApiState = { isLoaded: false, isLoading: false, error: apiError };
                    notifyStateChange();
                    reject(apiError);
                });
                return;
            }

            // Usar o novo inline bootstrap loader (melhor prática atual)
            const script = document.createElement('script');
            script.innerHTML = `
                (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.googleapis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
                    key: "${apiKey}",
                    v: "weekly"
                });
            `;
            document.head.appendChild(script);

            // Aguardar um momento para o bootstrap loader configurar window.google.maps
            await new Promise(resolve => setTimeout(resolve, 100));

            // Usar importLibrary para carregar a biblioteca Places
            if (window.google?.maps?.importLibrary) {
                await window.google.maps.importLibrary("places");
                globalApiState = { isLoaded: true, isLoading: false, error: null };
                notifyStateChange();
                resolve();
            } else {
                // Fallback para o método tradicional se importLibrary não estiver disponível
                const fallbackScript = document.createElement('script');
                fallbackScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
                fallbackScript.async = true;
                fallbackScript.defer = true;

                fallbackScript.onload = () => {
                    if (window.google?.maps?.places) {
                        globalApiState = { isLoaded: true, isLoading: false, error: null };
                        notifyStateChange();
                        resolve();
                    } else {
                        const loadError = new Error('Google Maps API carregada mas biblioteca Places não disponível');
                        globalApiState = { isLoaded: false, isLoading: false, error: loadError };
                        notifyStateChange();
                        reject(loadError);
                    }
                };

                fallbackScript.onerror = () => {
                    const scriptError = new Error('Falha ao carregar script do Google Maps API');
                    globalApiState = { isLoaded: false, isLoading: false, error: scriptError };
                    notifyStateChange();
                    reject(scriptError);
                };

                document.head.appendChild(fallbackScript);
            }

        } catch (error) {
            const unexpectedError = error instanceof Error ? error : new Error('Erro inesperado ao carregar Google Maps API');
            globalApiState = { isLoaded: false, isLoading: false, error: unexpectedError };
            notifyStateChange();
            reject(unexpectedError);
        }
    });

    try {
        await loadingPromise;
    } finally {
        loadingPromise = null;
    }
};

/**
 * Notifica todos os componentes registrados sobre mudanças no estado da API
 */
const notifyStateChange = (): void => {
    stateChangeCallbacks.forEach(callback => {
        try {
            callback({ ...globalApiState });
        } catch (error) {
            console.error('Erro ao notificar mudança de estado da Google Maps API:', error);
        }
    });
};

/**
 * Hook para usar a Google Maps API com lazy loading automático
 * 
 * Este hook gerencia o carregamento da API de forma global e compartilhada.
 * A primeira chamada inicia o carregamento, e as subsequentes reutilizam o mesmo estado.
 * 
 * @param autoLoad - Se true (padrão), inicia o carregamento automaticamente
 * @returns Estado atual da API e função para forçar carregamento
 */
export const useGoogleMapsApi = (autoLoad: boolean = true) => {
    const [apiState, setApiState] = useState<GoogleMapsApiState>(() => ({ ...globalApiState }));

    useEffect(() => {
        // Registrar callback para receber atualizações do estado global
        const handleStateChange = (newState: GoogleMapsApiState) => {
            setApiState(newState);
        };

        stateChangeCallbacks.add(handleStateChange);

        // Iniciar carregamento automático se solicitado
        if (autoLoad && !globalApiState.isLoaded && !globalApiState.isLoading) {
            loadGoogleMapsApi().catch(error => {
                console.error('Erro no carregamento automático da Google Maps API:', error);
            });
        }

        // Cleanup: remover callback quando componente desmonta
        return () => {
            stateChangeCallbacks.delete(handleStateChange);
        };
    }, [autoLoad]);

    // Função para forçar carregamento manual
    const loadApi = async (): Promise<void> => {
        return loadGoogleMapsApi();
    };

    return {
        isLoaded: apiState.isLoaded,
        isLoading: apiState.isLoading,
        error: apiState.error,
        loadApi
    };
};

/**
 * Função utilitária para verificar se a API está carregada (uso externo)
 */
export const isGoogleMapsApiLoaded = (): boolean => {
    return globalApiState.isLoaded;
};

/**
 * Função utilitária para obter o estado atual da API (uso externo)
 */
export const getGoogleMapsApiState = (): GoogleMapsApiState => {
    return { ...globalApiState };
};

// Tipagens para o window.google
declare global {
    interface Window {
        google?: {
            maps?: {
                importLibrary?: (library: string) => Promise<any>;
                places?: any;
                [key: string]: any;
            };
            [key: string]: any;
        };
    }
} 